/**
 * ABOUTME: Wrapper for the SmartQuote component.
 * ABOUTME: Handles feature flags and provides a consistent interface.
 */
import React, { useState, useEffect } from 'react';
import { SmartQuote } from './SmartQuote';
import { Tradesperson } from '@/lib/types';
import { config } from '@/lib/config';
import { debug } from '@/lib/utils';

interface SmartQuoteWrapperProps {
  initialValue?: string;
  postcode?: string;
  contextCategory?: string; // Kept for compatibility
  tradespeople?: Tradesperson[];
  isLoading?: boolean;
  onComplete?: () => void;
  onStateChange?: (state: 'selection' | 'contact' | 'success') => void;
  onDataLoaded?: (tradespeople: Tradesperson[]) => void;
}

export function SmartQuoteWrapper(props: SmartQuoteWrapperProps) {
  // State to handle tradespeople data
  const [loadedTradespeople, setLoadedTradespeople] = useState<Tradesperson[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Log props for debugging
  useEffect(() => {
    debug.log('SmartQuoteWrapper: Received props', {
      hasTradespeopleData: Boolean(props.tradespeople && props.tradespeople.length > 0),
      tradeCount: props.tradespeople?.length || 0,
      isLoading: props.isLoading,
      loadedCount: loadedTradespeople.length
    });
  }, [props.tradespeople, props.isLoading, loadedTradespeople.length]);

  // Handle the onDataLoaded callback if provided
  useEffect(() => {
    if (props.onDataLoaded && loadedTradespeople.length > 0) {
      debug.log('SmartQuoteWrapper: Calling onDataLoaded with loaded tradespeople', {
        count: loadedTradespeople.length
      });
      // Pass back any loaded tradespeople data to the parent component
      props.onDataLoaded(loadedTradespeople);
    }
  }, [loadedTradespeople, props.onDataLoaded]);

  // If the feature is disabled, render a coming soon message
  if (!config.features.enableTradeRequest) {
    return (
      <div className="w-full">
        <div className="bg-white border-2 border-purple-300 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 text-center">
            <h3 className="text-lg font-medium text-purple-800 mb-2">Coming Soon!</h3>
            <p className="text-purple-600 mb-4">
              Our trade request feature is currently under development and will be available soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Determine which tradespeople data to use:
  // 1. Use provided data if available
  // 2. Otherwise use data loaded by this component
  // 3. Fall back to an empty array if neither is available
  const finalTradespeople = props.tradespeople?.length
    ? props.tradespeople
    : loadedTradespeople.length
      ? loadedTradespeople
      : [];

  debug.log('SmartQuoteWrapper: Using final tradespeople data', {
    count: finalTradespeople.length,
    source: props.tradespeople?.length ? 'props' : loadedTradespeople.length ? 'loaded' : 'empty'
  });

  // Prepare safe props to pass to SmartQuote
  const safeProps = {
    ...props,
    isLoading: props.isLoading || isDataLoading,
    tradespeople: finalTradespeople
  };

  // Return the SmartQuote component with proper props
  return <SmartQuote {...safeProps} />;
}